'use client';

/**
 * ClarificationSection — schema-driven AI clarification banner.
 *
 * Renders the amber "the assistant needs more info" message that
 * narrative-search modes (agents, obeya simulations) surface when
 * `ki_ask` returns a `clarification` status. Reads
 * `clarification` from `<ExplorerSearchProvider />`; renders nothing
 * when no message is set or when no provider is mounted.
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.CLARIFICATION
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer
 * @layer: presentation
 */

import { MessageCircle } from 'lucide-react';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';
import { useExplorerSearch } from './explorer-search-context';

interface ClarificationSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function ClarificationSection(_props: ClarificationSectionProps) {
  const search = useExplorerSearch();
  if (!search?.clarification) return null;

  return (
    <div className="px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
      <div className="flex items-start gap-2">
        <MessageCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {search.clarification}
        </p>
      </div>
    </div>
  );
}
